import { useCallback, useEffect, useState } from "react";
import { House, Buildings, Sparkle, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWhere } from "@/components/RideExtras";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export default function Housing() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [siteId, setSiteId] = useState("");
  const [site, setSite] = useState({ name: "", gate: "", radius_miles: 5, min_per_mile: 0, min_per_seat: 0, shifts: "" });
  const [home, setHome] = useState({ kind: "apartment or flat", title: "", where: "", rent_per_month: 0, deposit: 0, rooms: 1, furnishing: "furnished", parking: false, bills_included: false, free_from: "" });
  const [want, setWant] = useState({ budget: 800, shifts: "days, 6 to 2", has_car: false, rooms: 1 });
  const [picks, setPicks] = useState(null);
  const { where, take, busy: locating } = useWhere();

  const load = useCallback(() => {
    api.get(`/workplace${siteId ? `?site_id=${siteId}` : ""}`)
      .then(({ data: d }) => { setData(d); setSiteId((s) => s || d.sites?.[0]?.id || ""); })
      .catch(oops);
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); }
    catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the board…</p>;
  const chosen = data.sites.find((s) => s.id === siteId);

  return (
    <div className="space-y-6" data-testid="housing-page">
      <div>
        <p className="overline text-[#B3241B]">Living near work</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <House size={30} weight="duotone" className="text-[#002FA7]" /> Near enough to walk in
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="housing-explainer">
          An employer registers the site and publishes the least a colleague may be paid for a run
          to it — nobody undercut, nobody overcharged. Then landlords list what is to let within
          the site's radius, because the cure for a chaotic car park is not more tarmac.
        </p>
      </div>

      <div className="border border-border bg-white p-4" data-testid="site-box">
        <p className="overline text-slate-500">
          <Buildings size={13} className="mr-1 inline align-[-2px] text-[#002FA7]" /> Workplaces
        </p>
        <select data-testid="site-pick" value={siteId} aria-label="Workplace"
          onChange={(e) => setSiteId(e.target.value)}
          className="tap-safe mt-2 w-full border border-border bg-white px-3 py-2 text-sm">
          <option value="">All workplaces</option>
          {data.sites.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.gate}</option>)}
        </select>
        {chosen && (
          <p className="mt-2 text-sm text-slate-700" data-testid="site-rate">
            Published rate at {chosen.name}: {chosen.symbol}{Number(chosen.min_per_mile || 0).toFixed(2)} a mile,
            {" "}{chosen.symbol}{Number(chosen.min_per_seat || 0).toFixed(2)} a seat ·
            {" "}{chosen.radius_miles} mile radius · {(chosen.people || []).length} registered
            <Button className="ml-2" size="sm" variant="outline" data-testid="site-join" disabled={busy}
              onClick={() => act(() => api.post(`/workplace/sites/${chosen.id}/join`), "You are on that workplace")}>
              Register me here
            </Button>
          </p>
        )}

        <div className="mt-4 border-t border-border pt-3">
          <p className="overline text-slate-500">Register a workplace</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Input data-testid="site-name" placeholder="Workplace name"
              value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} />
            <Input data-testid="site-gate" placeholder="Gate address or town"
              value={site.gate} onChange={(e) => setSite({ ...site, gate: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Radius (miles)
              <Input data-testid="site-radius" type="number" min={0.5} max={50} value={site.radius_miles}
                onChange={(e) => setSite({ ...site, radius_miles: e.target.value })} className="w-20" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Min per mile
              <Input data-testid="site-permile" type="number" min={0} step="0.01" value={site.min_per_mile}
                onChange={(e) => setSite({ ...site, min_per_mile: e.target.value })} className="w-24" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Min per seat
              <Input data-testid="site-perseat" type="number" min={0} step="0.01" value={site.min_per_seat}
                onChange={(e) => setSite({ ...site, min_per_seat: e.target.value })} className="w-24" />
            </label>
            <Input data-testid="site-shifts" placeholder="Shifts worked (optional)"
              value={site.shifts} onChange={(e) => setSite({ ...site, shifts: e.target.value })} />
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="site-gps" disabled={locating} onClick={take}>
              <Crosshair size={14} className="mr-1.5" /> Pin the gate here
            </Button>
            <Button size="sm" data-testid="site-save" disabled={busy || site.name.trim().length < 2}
              onClick={() => act(async () => {
                await api.post("/workplace/sites", {
                  ...site, radius_miles: Number(site.radius_miles) || 5,
                  min_per_mile: Number(site.min_per_mile) || 0,
                  min_per_seat: Number(site.min_per_seat) || 0,
                  lat: where?.lat, lng: where?.lng,
                });
                setSite({ ...site, name: "", gate: "" });
              }, "Workplace registered")}>
              Register the workplace
            </Button>
            {chosen && (
              <Button size="sm" variant="outline" data-testid="site-rate-suggest" disabled={busy}
                onClick={async () => {
                  try {
                    const { data: s } = await api.post("/workplace/sites/rate-suggest", { site_id: chosen.id });
                    setSite({ ...site, min_per_mile: s.min_per_mile, min_per_seat: s.min_per_seat });
                    toast.success(s.line);
                  } catch (e) { oops(e); }
                }}>
                <Sparkle size={14} className="mr-1.5" /> Suggest a fair rate
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="border border-border bg-white p-4" data-testid="housing-match-box">
        <p className="overline text-slate-500">
          <Sparkle size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Find me somewhere
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-4">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Budget
            <Input data-testid="match-budget" type="number" min={0} value={want.budget}
              onChange={(e) => setWant({ ...want, budget: e.target.value })} className="w-24" />
          </label>
          <Input data-testid="match-shifts" placeholder="Shifts you work"
            value={want.shifts} onChange={(e) => setWant({ ...want, shifts: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Rooms
            <Input data-testid="match-rooms" type="number" min={0} value={want.rooms}
              onChange={(e) => setWant({ ...want, rooms: e.target.value })} className="w-20" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="match-car" checked={want.has_car}
              onChange={(e) => setWant({ ...want, has_car: e.target.checked })} />
            I have a car
          </label>
        </div>
        <Button className="mt-2" size="sm" data-testid="match-go" disabled={busy || !siteId}
          onClick={() => act(async () => {
            const { data: d } = await api.post("/workplace/homes/match", {
              site_id: siteId, budget: Number(want.budget) || 0, shifts: want.shifts,
              has_car: want.has_car, rooms: Number(want.rooms) || 0 });
            setPicks(d);
          }, "Had a look round")}>
          Match me
        </Button>
        {picks && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="match-out">
            <p className="text-sm text-[#0F172A]">{picks.line}</p>
            <ul className="mt-1 space-y-1 text-sm text-slate-700">
              {picks.picks.map((p) => (
                <li key={p.home_id} data-testid={`match-pick-${p.home_id}`}>
                  <span className="font-semibold text-[#0F172A]">{p.fit}</span> · {p.why}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div data-testid="housing-homes">
        <p className="overline text-slate-500">To let near the gate</p>
        {data.homes.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing listed yet — put yours up below.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.homes.map((h) => (
              <div key={h.id} className="border border-border bg-white p-3" data-testid={`home-${h.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{h.title}</p>
                <p className="text-xs text-slate-500">
                  {h.kind} · {h.rooms} room(s) · {h.furnishing}
                  {h.parking ? " · parking" : " · no parking"}
                  {h.miles_from_gate != null ? ` · ${h.miles_from_gate} mi from the gate` : ""}
                  {h.walk_minutes ? ` · ${h.walk_minutes} min walk` : ""}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {h.symbol}{Number(h.rent_per_month || 0).toFixed(0)} a month · free from {h.free_from}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {h.owner_id !== data.me.id && (
                    <>
                      <Button size="sm" variant="outline" data-testid={`home-ask-${h.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/workplace/homes/${h.id}/enquire`, { message: "Is it still free?" }), "Asked")}>
                        Ask about it
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`home-hold-${h.id}`} disabled={busy}
                        onClick={() => act(() => api.post("/workplace/homes/hold", { home_id: h.id, weeks: 1 }), "Held for you")}>
                        Hold it
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-white p-4" data-testid="home-list-yours">
        <p className="overline text-slate-500">Landlords and owners — put a place up</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <Input data-testid="home-title" placeholder="Title — 'Two bed flat, Maple Street'"
            value={home.title} onChange={(e) => setHome({ ...home, title: e.target.value })} />
          <select data-testid="home-kind" value={home.kind} aria-label="What kind"
            onChange={(e) => setHome({ ...home, kind: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.home_kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <Input data-testid="home-where" placeholder="Where is it?"
            value={home.where} onChange={(e) => setHome({ ...home, where: e.target.value })} />
          <select data-testid="home-furnishing" value={home.furnishing} aria-label="Furnishing"
            onChange={(e) => setHome({ ...home, furnishing: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.furnishing.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Rent a month
            <Input data-testid="home-rent" type="number" min={0} value={home.rent_per_month}
              onChange={(e) => setHome({ ...home, rent_per_month: e.target.value })} className="w-24" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Rooms
            <Input data-testid="home-rooms" type="number" min={0} value={home.rooms}
              onChange={(e) => setHome({ ...home, rooms: e.target.value })} className="w-20" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" data-testid="home-parking" checked={home.parking}
              onChange={(e) => setHome({ ...home, parking: e.target.checked })} />
            Parking included
          </label>
          <Input data-testid="home-free" placeholder="Free from"
            value={home.free_from} onChange={(e) => setHome({ ...home, free_from: e.target.value })} />
        </div>
        <Button className="mt-2" size="sm" data-testid="home-save"
          disabled={busy || home.title.trim().length < 3}
          onClick={() => act(async () => {
            const { data: d } = await api.post("/workplace/homes", {
              ...home, site_id: siteId, rent_per_month: Number(home.rent_per_month) || 0,
              rooms: Number(home.rooms) || 0, lat: where?.lat, lng: where?.lng,
            });
            setHome({ ...home, title: "", where: "" });
            return d;
          }, "On the board — it goes live once approved")}>
          Put it on the board
        </Button>
        {(data.my_homes || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-sm text-slate-700" data-testid="home-mine">
            {data.my_homes.map((h) => (
              <li key={h.id}>
                {h.title} · {h.approved ? "on the board" : "waiting for approval"}
                {h.miles_from_gate != null ? ` · ${h.miles_from_gate} mi from the gate` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>

      {(data.enquiries || []).length > 0 && (
        <div data-testid="housing-enquiries">
          <p className="overline text-slate-500">Enquiries</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.enquiries.map((e) => (
              <li key={e.id} data-testid={`enquiry-${e.id}`}>
                {e.title} · {e.asker_name} · {e.message} · {e.status}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
